import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getUser } from "../../utils/storage";
import { axiosInstance } from "../../utils/axios";
import { CameraIcon, PlusIcon, ArrowPathIcon } from "@heroicons/react/20/solid";
import AdminSidebar from "../../components/AdminSidebar";
import ComboboxSearch from "../../components/form/ComboboxSearch";
import NumberInput from "../../components/form/NumberInput";
import AutoOcr from "../../components/AutoOcr";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import useDialogStore from "../../stores/useDialogStore";
import { useForm } from "react-hook-form";
import LoadingComponent from "../../components/LoadingComponent";
import Button from "../../components/Button";

export default function DeviceForm() {
  const user = getUser();
  const userId = user.id;
  const [brand, setBrand] = useState("");
  const [device, setDevice] = useState(null);
  const [setshowOCR] = useState(false);

  // Hooks and Utilities
  const { openDialog, closeDialog } = useDialogStore();
  const queryClient = useQueryClient();
  const { objID } = useParams();
  const [searchParams] = useSearchParams();
  const next_unique_id = searchParams.get("next_unique_id");
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    watch,
    setError,
    clearErrors,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      quantity: 1,
      stock: 1,
      counter_black: "",
      counter_color: "",
      palette: "",
      device_brand_id: null,
      device_model_id: null,
      serial_no: "",
      user_id: userId,
      files: [],
      created_at: new Date().toISOString().split("T")[0],
      comment: "",
      // Options
      finisher: 0,
      rt_lct: 0,
      blue: 0,
      green: 0,
      cabinet: 0,
      no_df: 0,
      spare_parts: null,
      trays: 4,
      notice_required: "0",
      recycled_date: null,
    },
  });

  const {
    data: brandsData,
    isLoading: isLoadingBrands,
    error: brandsError,
  } = useQuery({
    queryKey: ["device-brands"],
    queryFn: async () => {
      const response = await axiosInstance.get(
        window.REACT_APP_API_ENDPOINT + "/device-brands?type=all"
      );
      return response.data.data;
    },
  });

  const device_brand_id = watch("device_brand_id");
  const watchedValues = watch();

  const {
    data: modelsData,
    isLoading: isLoadingModels,
    error: modelsError,
    refetch: refetchModels,
  } = useQuery({
    queryKey: ["device-models", device_brand_id],
    queryFn: async () => {
      if (!device_brand_id) return [];
      const response = await axiosInstance.get(
        window.REACT_APP_API_ENDPOINT +
          "/device-brands/" +
          device_brand_id +
          "/models"
      );
      return response.data;
    },
    enabled: !!device_brand_id,
  });

  useEffect(() => {
    refetchModels();
  }, [objID]);

  const {
    data: deviceData,
    isLoading: isLoadingDevice,
    error: deviceError,
  } = useQuery({
    queryKey: ["device", objID],
    queryFn: async () => {
      const response = await axiosInstance.get(
        `${window.REACT_APP_API_ENDPOINT}/devices/${objID}`
      );
      return response.data.device;
    },
    enabled: !!objID,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Effects
  useEffect(() => {
    if (deviceData && objID) {
      setDevice(deviceData);

      // Convert ISO date to YYYY-MM-DD format for date input
      const formatDateForInput = (dateString) => {
        if (!dateString) return "";
        return new Date(dateString).toISOString().split("T")[0];
      };

      reset({
        ...deviceData,
        ...(deviceData.device_options || {}),
        user_id: deviceData.user_id,
        created_at: formatDateForInput(deviceData.created_at),
        recycled_date: deviceData.device_options?.recycled_date || "",
      });
      setBrand(deviceData.brand_name);
    }
  }, [deviceData, objID, reset]);

  // Mutations
  const createDeviceOptionsMutation = useMutation({
    mutationFn: async ({ devices, deviceOptions }) => {
      // Store devices[0].id in a variable for later use
      const firstDeviceId = devices[0]?.id;

      if (getValues("quantity") > 1) {
        const deviceIds = devices.map((device) => device.id);
        return axiosInstance.post(
          window.REACT_APP_API_ENDPOINT + "/device-options/multiple-update",
          {
            device_ids: deviceIds,
            device_options: deviceOptions,
          }
        );
      } else {
        return axiosInstance.post(
          window.REACT_APP_API_ENDPOINT + "/device-options",
          {
            ...deviceOptions,
            device_id: firstDeviceId,
          }
        );
      }
    },
    onSuccess: (response, { devices }) => {
      if (getValues("quantity") > 1) {
        navigate(
          `/devices/multi-device-details?&ids=${response.data.results.map(
            (device) => device.device_id
          )}`
        );
      } else {
        navigate(`/devices/${devices[0].id}/details`);
      }
    },
    onError: (error) => {
      console.error("Failed to create device options:", error);
    },
  });

  const createDeviceMutation = useMutation({
    mutationFn: async ({ deviceData, deviceOptions }) => {
      console.log("formData", deviceData);
      console.log("deviceOptions", deviceOptions);
      return axiosInstance.post(
        window.REACT_APP_API_ENDPOINT + "/devices",
        deviceData
      );
    },
    onSuccess: (response, variables) => {
      setDevice(response.data.devices);
      toast.success("Device created successfully");
      createDeviceOptionsMutation.mutate({
        devices: response.data.devices,
        deviceOptions: variables.deviceOptions,
      });
      queryClient.invalidateQueries(["devices"]);
    },
    onError: (error) => {
      toast.error("Failed to create device. Please try again.");
    },
  });

  const updateDeviceOptionsMutation = useMutation({
    mutationFn: async ({ id, deviceOptions }) => {
      return axiosInstance.patch(
        window.REACT_APP_API_ENDPOINT + `/device-options/patch/${id}`,
        {
          ...deviceOptions,
          device_id: device.id,
        }
      );
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries(["devices"]);
      navigate(`/devices/${objID}/details`);
    },
    onError: (error) => {},
  });

  const updateDeviceMutation = useMutation({
    mutationFn: async ({ id, deviceData, deviceOptions }) => {
      return axiosInstance.patch(
        window.REACT_APP_API_ENDPOINT + `/devices/patch/${id}`,
        deviceData
      );
    },
    onSuccess: (response, variables) => {
      setDevice(response.data);
      updateDeviceOptionsMutation.mutate({
        id: response.data.id,
        deviceOptions: variables.deviceOptions,
      });
      queryClient.invalidateQueries(["devices"]);
    },
    onError: (error) => {
      toast.error("Failed to update device. Please try again.");
    },
  });

  const {
    mutateAsync: checkSerialNumberMutation,
    isPending: isCheckingSerialNumber,
  } = useMutation({
    mutationFn: async (serial) => {
      const response = await axiosInstance.post(
        `${window.REACT_APP_API_ENDPOINT}/devices/check-serial-number?serial_no=${serial}`
      );
      return response.data;
    },
    onError: (error) => {
      setError("Failed to check serial number. Please try again.");
    },
  });
  const handleFinalSubmit = async (e) => {
    e.preventDefault();
    const data = getValues();

    if (data.serial_no && objID === undefined) {
      const result = await checkSerialNumberMutation(data.serial_no);
      console.log("result", result.messages);
      if (result.exists) {
        openDialog({
          title: "Serial Number Duplicate Check",
          content: (
            <SerialNumberDuplicateCheck
              onClose={() => {
                closeDialog();
              }}
              onSubmit={() => {
                onSubmit();
                closeDialog();
              }}
              messages={result?.messages}
            />
          ),
        });
      } else {
        onSubmit();
      }
    } else {
      onSubmit();
    }
  };
  // Handlers
  const handleBrandSelect = (brand) => {
    setValue("device_brand_id", brand.id);
    setBrand(brand.name);
    clearErrors("device_brand_id");
  };

  const handleSelectedModelChange = (selectedItem) => {
    if (selectedItem != undefined) {
      setValue("device_model_id", selectedItem.id);
      clearErrors("device_model_id");
    }
  };

  const handleOcrSerial = (number) => setValue("serial_no", number);
  const handleOcrCounterBlack = (number) => setValue("counter_black", number);
  const handleOcrCounterColor = (number) => setValue("counter_color", number);

  const onSubmit = handleSubmit((data) => {
    let hasError = false;
    if (!data.device_brand_id) {
      setError("device_brand_id", {
        type: "manual",
        message: "Please select a brand",
      });
      hasError = true;
    }
    if (data.device_brand_id && !data.device_model_id) {
      setError("device_model_id", {
        type: "manual",
        message: "Please select a model for the selected brand",
      });
      hasError = true;
    }

    if (hasError) return;
    const deviceOptions = {
      finisher: data.finisher,
      rt_lct: data.rt_lct,
      blue: data.blue,
      green: data.green,
      cabinet: data.cabinet,
      no_df: data.no_df,
      spare_parts: data.spare_parts,
      trays: data.trays,
      notice_required: data.notice_required,
      notice_text: data.comment,
      recycled_date: data.recycled_date,
    };

    // Device data (excluding options)
    const deviceData = {
      serial_no: data.serial_no,
      counter_black: parseInt(data.counter_black),
      counter_color: parseInt(data.counter_color),
      total_counter:
        parseInt(data.counter_black) + parseInt(data.counter_color),
      quantity: data.quantity,
      stock: data.stock,
      palette: data.palette,
      device_brand_id: data.device_brand_id,
      device_model_id: data.device_model_id,
      user_id: userId,
      created_at: data.created_at,
      comment: data.comment,
      files: data.files,
    };

    if (objID) {
      updateDeviceMutation.mutate({
        id: objID,
        deviceData: deviceData,
        deviceOptions: deviceOptions,
      });
    } else {
      createDeviceMutation.mutate({
        deviceData: deviceData,
        deviceOptions: deviceOptions,
      });
    }
  });

  // Conditional Rendering
  if (isLoadingDevice && objID) {
    return (
      <AdminSidebar>
        <LoadingComponent />
      </AdminSidebar>
    );
  }

  if (deviceError && objID) {
    return (
      <AdminSidebar>
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          Failed to fetch device data. Please try again.
        </div>
      </AdminSidebar>
    );
  }

  const DEVICE_OPTIONS = [
    {
      key: "finisher",
      label: "Finisher",
      exclusive: [],
      color: "white",
    },
    {
      key: "rt_lct",
      label: "RT / LCT",
      exclusive: [],
      color: "white",
    },
    {
      key: "no_df",
      label: "No DF",
      exclusive: [],
      color: "white",
    },
    {
      key: "blue",
      label: "Blue",
      exclusive: ["green"],
      color: "blue",
    },
    {
      key: "green",
      label: "Green",
      exclusive: ["blue"],
      color: "green",
    },
    {
      key: "cabinet",
      label: "Cabinet",
      exclusive: [],
      color: "white",
    },
  ];

  const COLOR_STYLES = {
    green: {
      selectedBg: "bg-green-200",
      selectedBorder: "border-green-600",
      selectedDot: "bg-green-600",
    },
    blue: {
      selectedBg: "bg-blue-200",
      selectedBorder: "border-blue-600",
      selectedDot: "bg-blue-600",
    },
    white: {
      selectedBg: "bg-gray-200",
      selectedBorder: "border-gray-600",
      selectedDot: "bg-gray-600",
    },
  };

  const OptionButton = React.memo(({ label, checked, onClick, color }) => {
    const styles = COLOR_STYLES[color] || COLOR_STYLES.white;

    return (
      <div
        className={`flex items-center px-4 border rounded h-8 mr-2 mb-2 cursor-pointer ${
          checked ? styles.selectedBg : "bg-white"
        } ${checked ? styles.selectedBorder : "border-textBlue"}`}
        onClick={onClick}
      >
        <div
          className={`w-4 h-4 border rounded-full mr-2 ${
            checked ? styles.selectedDot : "bg-white border-textBlue"
          }`}
        />
        {label}
      </div>
    );
  });

  return (
    <AdminSidebar>
      <div className="bg-white pb-8">
        <div className="bg-lightBlue px-4 sm:px-6 lg:px-8 py-4">
          <h2 className="text-textBlue text-lg flex font-bold items-center">
            <span className="mr-2">
              {objID ? "Edit Device" : "Register a Device"}
            </span>{" "}
            <PlusIcon className="w-6" />
          </h2>
        </div>
        <div className="mt-8 flow-root px-4 sm:px-6 lg:px-8">
          <div className="">
            <div className="inline-block min-w-full py-2 align-middle">
              <form onSubmit={(e) => handleFinalSubmit(e)}>
                <h4 className="mb-4 font-bold">Brand</h4>
                <div className="flex flex-wrap">
                  {isLoadingBrands ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                      <span className="ml-2 text-gray-600">
                        Loading brands...
                      </span>
                    </div>
                  ) : brandsError ? (
                    <div className="text-red-500">Failed to load brands</div>
                  ) : (
                    brandsData?.map((brand) => (
                      <div
                        key={brand.id}
                        className={`flex items-center px-4 border border-textBlue rounded dark:border-textBlue h-8 mr-2 mb-2 ${
                          device_brand_id === brand.id
                            ? "bg-blue-200"
                            : "bg-white"
                        }`}
                        onClick={() => handleBrandSelect(brand)}
                      >
                        <div
                          className={`w-4 h-4 border border-textBlue rounded-full mr-2 ${
                            device_brand_id === brand.id
                              ? "bg-blue-600"
                              : "bg-white"
                          }`}
                        />
                        {brand.name}
                      </div>
                    ))
                  )}
                </div>
                <p className="text-red-500 text-xs mb-6 pt-2">
                  {errors?.device_brand_id?.message}
                </p>

                <h4 className="mb-4 font-bold">Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                  {modelsData?.length > 0 && (
                    <div>
                      <h6 className="text-sm font-bold">Model</h6>
                      <div className="relative">
                        {isLoadingModels ? (
                          <div className="flex items-center">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                            <span className="ml-2 text-gray-600">
                              Loading models...
                            </span>
                          </div>
                        ) : modelsError ? (
                          <div className="text-red-500">
                            Failed to load models
                          </div>
                        ) : (
                          <ComboboxSearch
                            key={modelsData?.id}
                            title="Model"
                            className={`w-full ${
                              errors.device_model_id ? "ring-red-500" : ""
                            }`}
                            model={getValues("device_model_id")}
                            brand={device_brand_id}
                            onValuesChange={() => {}}
                            values={modelsData}
                            onSelectedItemChange={handleSelectedModelChange}
                          />
                        )}
                        {errors.device_model_id && (
                          <p className="text-red-500 text-xs mt-1">
                            {errors.device_model_id.message}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="relative">
                    <h6 className="text-sm font-bold">Serial Number</h6>
                    <input
                      type="text"
                      id="form_serial-no"
                      placeholder="Serial Number"
                      {...register("serial_no")}
                      onChange={(e) => {
                        setValue("serial_no", e.target.value);
                      }}
                      className={`w-full rounded-md border-0 bg-white py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ${
                        errors.serial_no ? "ring-red-500" : "ring-gray-300"
                      } focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6`}
                    />
                    <CameraIcon
                      className="w-6 absolute top-6 right-2"
                      onClick={() =>
                        openDialog({
                          title: "OCR",
                          content: (
                            <AutoOcr
                              onSerialNumber={handleOcrSerial}
                              onCounterBlack={handleOcrCounterBlack}
                              onCounterColor={handleOcrCounterColor}
                              onClose={() => setshowOCR(false)}
                              brand={brand}
                              onDeviceImages={(files) =>
                                setValue("files", files)
                              }
                            />
                          ),
                        })
                      }
                    />
                    {/* {isCheckingSerialNumber && (
                      <p className="text-gray-500 text-sm mt-1">
                        Checking serial number...
                      </p>
                    )} */}
                    {/* {errors.serial_no && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.serial_no.message}
                      </p>
                    )} */}
                  </div>
                  <div>
                    <h6 className="text-sm font-bold">Counter Color</h6>
                    <input
                      type="number"
                      id="form_counter-color"
                      placeholder="Counter Color"
                      value={watchedValues.counter_color}
                      onChange={(e) =>
                        setValue("counter_color", e.target.value)
                      }
                      className="w-full rounded-md border-0 bg-white py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    />
                    {/* No required error for counter_color */}
                  </div>
                  <div>
                    <h6 className="text-sm font-bold">Counter Black</h6>
                    <input
                      type="number"
                      id="form_counter-black"
                      placeholder="Counter Black"
                      value={watchedValues.counter_black}
                      onChange={(e) =>
                        setValue("counter_black", e.target.value)
                      }
                      className="w-full rounded-md border-0 bg-white py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    />
                    {/* No required error for counter_black */}
                  </div>
                  <div>
                    <h6 className="text-sm font-bold">Register Date</h6>
                    <input
                      type="date"
                      placeholder="Created At"
                      {...register("created_at")}
                      className="w-full rounded-md border-0 bg-white h-9 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    />
                    {errors.created_at && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.created_at.message}
                      </p>
                    )}
                  </div>

                  {(next_unique_id || device?.unique_id) && (
                    <div>
                      <h6 className="text-sm font-bold">Unique ID</h6>
                      <input
                        type="text"
                        disabled={true}
                        placeholder="Unique ID"
                        className="w-full rounded-md border-0 bg-gray-100 py-1.5 px-3 text-gray-500 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        value={
                          device?.unique_id ? device?.unique_id : next_unique_id
                        }
                      />
                    </div>
                  )}
                </div>
                <div>
                  <h4 className="mb-4 font-bold">Options</h4>
                  <div className="flex flex-wrap">
                    {DEVICE_OPTIONS.map((opt) => (
                      <OptionButton
                        key={opt.key}
                        label={opt.label}
                        checked={watchedValues[opt.key]}
                        color={opt.color}
                        onClick={() => {
                          const newValue = !watchedValues[opt.key];
                          setValue(opt.key, newValue);
                          // If turning on, turn off exclusives
                          if (newValue && opt.exclusive) {
                            opt.exclusive.forEach((exKey) =>
                              setValue(exKey, false)
                            );
                          }
                        }}
                      />
                    ))}
                  </div>

                  <div className="flex flex-wrap mb-4">
                    <div className="flex items-center px-4 border border-textBlue rounded dark:border-textBlue h-8 mr-2 bg-lightBlue">
                      <label
                        htmlFor="bordered-radio-1"
                        className="w-full py-4 text-sm font-medium text-gray dark:text-gray mr-2"
                      >
                        Trays
                      </label>
                      <div className="flex items-center space-x-4">
                        {[1, 2, 3, 4].map((option) => (
                          <div key={option} className="flex items-center">
                            <input
                              type="radio"
                              id={`trays-${option}`}
                              value={option}
                              checked={watchedValues.trays === option}
                              onChange={(e) =>
                                setValue("trays", parseInt(e.target.value))
                              }
                              className="w-4 h-4 text-blue-600 border-textBlue focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-textBlue focus:ring-2 dark:bg-textBlue dark:border-textBlue"
                            />
                            <label
                              htmlFor={`trays-${option}`}
                              className="ml-2 text-sm font-medium text-gray dark:text-gray"
                            >
                              {option}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="w-fit mb-6">
                    {!objID && (
                      <NumberInput
                        title={"Quantity"}
                        value={watchedValues.quantity}
                        onChange={(value) =>
                          setValue("quantity", parseInt(value))
                        }
                      />
                    )}
                    <NumberInput
                      title={"Stock"}
                      value={watchedValues.stock}
                      onChange={(value) => setValue("stock", parseInt(value))}
                    />
                    <input
                      type="text"
                      placeholder="Palette"
                      value={watchedValues.palette}
                      onChange={(e) => setValue("palette", e.target.value)}
                      className="w-full lg:w-1/2 rounded-md border-0 bg-white py-1.5 pl-3 pr-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <h4 className="mb-2">
                    Is a notice required for your device?
                  </h4>
                  <div className="flex">
                    <div class="flex items-center mb-4 mr-8">
                      <input
                        id="default-radio-1"
                        type="radio"
                        value="1"
                        name="default-radio"
                        class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                        checked={watchedValues.notice_required == 1}
                        onChange={(e) =>
                          setValue("notice_required", e.target.value)
                        }
                      />
                      <label
                        for="default-radio-1"
                        class="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                      >
                        Yes
                      </label>
                    </div>
                    <div class="flex items-center mb-4">
                      <input
                        id="default-radio-1"
                        type="radio"
                        value="0"
                        name="default-radio"
                        class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                        checked={watchedValues.notice_required == 0}
                        onChange={(e) =>
                          setValue("notice_required", e.target.value)
                        }
                      />
                      <label
                        for="default-radio-1"
                        class="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                      >
                        No
                      </label>
                    </div>
                  </div>
                  {watchedValues.notice_required == 1 && (
                    <div>
                      <textarea
                        value={watchedValues.comment}
                        onChange={(e) => setValue("comment", e.target.value)}
                        className="h-28 rounded-md border-0 bg-white py-1.5 pl-3 pr-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 mb-2"
                      />
                    </div>
                  )}
                </div>
                <div className="flex  gap-2 md:flex-row flex-col">
                  <div
                    className={`flex items-center px-4 border border-textBlue rounded dark:border-textBlue h-8 mr-2 mb-2 ${
                      watchedValues.spare_parts ? "bg-blue-200" : "bg-white"
                    }`}
                    onClick={() => {
                      setValue("spare_parts", !watchedValues.spare_parts);
                      if (
                        !watchedValues.spare_parts &&
                        watchedValues.recycled_date
                      ) {
                        setValue("recycled_date", "");
                      }
                    }}
                  >
                    <div
                      className={`w-4 h-4 border border-textBlue rounded-full mr-2 ${
                        watchedValues.spare_parts ? "bg-blue-600" : "bg-white"
                      }`}
                    />
                    Spare Parts
                  </div>
                  <div className="flex flex-col">
                    <div
                      className={`flex items-center px-4 border border-textBlue rounded dark:border-textBlue h-8 mr-2 mb-2 ${
                        watchedValues.recycled_date ? "bg-blue-200" : "bg-white"
                      }`}
                      onClick={() => {
                        setValue(
                          "recycled_date",
                          !watchedValues.recycled_date
                            ? new Date()
                                .toISOString()
                                .slice(0, 19)
                                .replace("T", " ")
                            : ""
                        );
                        if (
                          !watchedValues.recycled_date &&
                          watchedValues.spare_parts
                        ) {
                          setValue("spare_parts", null);
                        }
                      }}
                    >
                      <div
                        className={`w-4 h-4 border border-textBlue rounded-full mr-2 ${
                          watchedValues.recycled_date
                            ? "bg-blue-600"
                            : "bg-white"
                        }`}
                      />
                      Recycled
                    </div>
                    {watchedValues.recycled_date && (
                      <div className="mt-2">
                        <label className="text-sm font-medium text-gray-700 mb-1 block">
                          Recycled Date
                        </label>
                        <input
                          type="datetime-local"
                          value={watchedValues.recycled_date.replace(" ", "T")}
                          onChange={(e) =>
                            setValue(
                              "recycled_date",
                              e.target.value.replace("T", " ")
                            )
                          }
                          className="rounded-md border-0 bg-white py-1.5 pl-3 pr-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        />
                      </div>
                    )}
                  </div>
                </div>
                {/* General error message removed; field errors are now handled by react-hook-form */}
                <div className="flex w-full justify-end gap-2">
                  <button
                    type="submit"
                    loading={
                      createDeviceMutation.isPending ||
                      updateDeviceMutation.isPending ||
                      createDeviceOptionsMutation.isPending ||
                      updateDeviceOptionsMutation.isPending
                    }
                    className="rounded-md bg-bluePrimary h-9 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-bluePrimary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                  >
                    {createDeviceMutation.isPending ||
                    updateDeviceMutation.isPending ||
                    createDeviceOptionsMutation.isPending ||
                    updateDeviceOptionsMutation.isPending ? (
                      <div className="flex items-center gap-2">
                        <ArrowPathIcon className="w-5 h-5 animate-spin" />
                        Generating Barcode...
                      </div>
                    ) : (
                      "Generate Barcode"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </AdminSidebar>
  );
}

export function SerialNumberDuplicateCheck({ onClose, onSubmit, messages }) {
  return (
    <div>
      <h1 className="text-lg font-bold">Serial Number already exists</h1>
      <p className="text-sm">
        Are you sure you want to continue, with this serial number?
      </p>
      <ul className="text-sm list-disc pl-5 space-y-2">
        {messages &&
          messages.map((message, index) => (
            <li key={index} className="bg-gray-100 p-2 rounded-md shadow-sm">
              {message}
            </li>
          ))}
      </ul>
      <div className="flex justify-end gap-2 mt-4">
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={onSubmit}>
          Continue
        </Button>
      </div>
    </div>
  );
}
